import { useSelector } from "react-redux";
import { useState, useRef, useMemo, useCallback, ChangeEventHandler } from "react";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { Asset } from "app/types";
import { RootState } from "app/store";
import { usePickImageAsset } from "app/hook";
import I18n from "common/i18n";
import { btnDelay, usePlaySound, formatStorageUsage } from "common/utils";
import { useAssetLimit } from "features/subscription";
import { StockTabIndex } from "features/creator/types";
import { AIGenerateOnlyModal } from "features/generation";
import { getUserAssets, AssetsResponse } from "features/user/api";
import { SelectedImage } from "./types";

interface AssetPickerProps {
  onSelect: (image: SelectedImage) => void;
  onClose: () => void;
}

export const AssetPicker = ({ onSelect, onClose }: AssetPickerProps) => {
  const play = usePlaySound();
  const pickImageAsset = usePickImageAsset();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const user = useSelector((state: RootState) => state.user.active);
  const assetLimit = useAssetLimit();
  const isUnlimited = assetLimit.unlimited;
  const isOverLimit = !isUnlimited && assetLimit.used > assetLimit.limit;

  const tabs = [
    {
      key: StockTabIndex.ALBUM,
      icon: "photo_library",
      label: I18n.t("MSG_CREATOR_MENU_ASSET_CATEGORY_ALBUM_TITLE"),
    },
    {
      key: StockTabIndex.DRAWING,
      icon: "edit_square",
      label: I18n.t("MSG_CREATOR_MENU_ASSET_CATEGORY_DRAWING_TITLE"),
    },
    {
      key: StockTabIndex.AI,
      icon: "auto_awesome",
      label: I18n.t("MSG_USER_STOCK_AI_TAB"),
    },
  ];

  const [pickerTab, setPickerTab] = useState(StockTabIndex.ALBUM);
  const [showAIModal, setShowAIModal] = useState(false);

  const { data } = useQuery<AssetsResponse>({
    queryKey: ["creator/getUserAssets", user?.uid],
    queryFn: () => getUserAssets({ uid: user?.uid }),
    staleTime: Infinity,
    enabled: !!user?.uid,
    placeholderData: { assets: [], totalSize: 0 },
  });
  const assets = Array.isArray(data?.assets) ? data.assets : [];

  const isAIAsset = useCallback((asset: Asset) => asset.tags?.startsWith("ai_generated:"), []);

  const filteredAssets = useMemo(() => {
    return assets.filter((a) => {
      if (pickerTab === StockTabIndex.AI) return isAIAsset(a);
      if (pickerTab === StockTabIndex.DRAWING) return /.svg/.test(a.url) && !isAIAsset(a);
      return !/.svg/.test(a.url) && !isAIAsset(a);
    });
  }, [assets, pickerTab, isAIAsset]);

  const handleTabChange = (tab: StockTabIndex) => () => {
    play();
    setPickerTab(tab);
  };

  const handleSelectAsset = (asset: Asset) => () => {
    btnDelay(() => {
      play();
      onSelect({
        id: asset.id,
        url: asset.url,
        name: asset.name,
        pngUrl: asset.metadata?.png_url,
        metadata: asset.metadata,
      });
      onClose();
    });
  };

  const pickImage: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (file && user?.uid) {
      await pickImageAsset(
        file,
        user.uid,
        async () => {
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
        () => {
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      );
    }
  };

  const handleOpenAIModal = () => {
    btnDelay(() => {
      play();
      setShowAIModal(true);
    });
  };

  const handleCloseAIModal = () => {
    setShowAIModal(false);
  };

  const getEmptyMessage = () => {
    switch (pickerTab) {
      case StockTabIndex.ALBUM:
        return I18n.t("MSG_ASSET_ALBUM_EMPTY_MESSAGE");
      case StockTabIndex.DRAWING:
        return I18n.t("MSG_ASSET_DRAWING_EMPTY_MESSAGE");
      case StockTabIndex.AI:
        return I18n.t("MSG_USER_STOCK_AI_EMPTY");
      default:
        return "";
    }
  };

  return (
    <>
      <div className="absolute bottom-full left-0 right-0 flex-col-el h-[300px] bg-white overflow-hidden z-10">
        {/* 容量表示 */}
        {!isUnlimited && (
          <div className="flex-row-el shrink-0 w-full h-[28px] px-3 items-center justify-end bg-gray2-20 border-0 border-t border-solid border-orange-alt">
            <span className="material-symbols-outlined !text-[16px] text-gray-80 mr-1">
              cloud_upload
            </span>
            <p
              className={`text-body text-font-caption2-narrow ${isOverLimit ? "text-red-100" : "text-gray-80"}`}
            >
              {formatStorageUsage(assetLimit.used, assetLimit.limit, isUnlimited)}
            </p>
          </div>
        )}

        {/* タブバー */}
        <div className="flex-row-el shrink-0 w-full h-[50px]">
          {tabs.map((tab, index) => (
            <div
              key={tab.key}
              onClick={handleTabChange(tab.key)}
              className={`flex-row-el flex-center flex-1 gap-2 cursor-pointer transition-colors border-0 border-t border-b border-solid border-orange-alt ${
                index === 1 ? "border-l border-r" : ""
              } ${pickerTab === tab.key ? "bg-green-alt" : "bg-white hover:bg-green-20"}`}
            >
              <span
                className={`material-symbols-outlined !text-xl ${
                  pickerTab === tab.key ? "text-textcolor-black" : "text-gray-80"
                }`}
              >
                {tab.icon}
              </span>
              <p
                className={`text-body text-font-caption2-narrow ${
                  pickerTab === tab.key ? "text-textcolor-black" : "text-gray-80"
                }`}
              >
                {tab.label}
              </p>
            </div>
          ))}
        </div>

        {/* コンテンツエリア */}
        <div className="flex-col-el flex-1 w-full overflow-hidden">
          <div className="flex-col-el flex-1 w-full overflow-auto p-3">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-3 w-full">
              {/* アルバムタブ: アップロードボタン */}
              {pickerTab === StockTabIndex.ALBUM && (
                <div className="flex-col-el flex-center upsilon">
                  <div className="btn btn-primary !p-0 !m-0 w-[120px] h-[120px] cursor-pointer bg-green-100 rounded-lg">
                    <div className="flex-col-center w-full h-full">
                      <input
                        ref={fileInputRef}
                        type="file"
                        onClick={() => play()}
                        onChange={pickImage}
                        accept="image/*"
                        className="absolute w-[120px] h-[120px] opacity-0 z-50 cursor-pointer"
                      />
                      <span className="material-symbols-outlined text-white !text-[40px]">
                        add_photo_alternate
                      </span>
                      <p className="text-body text-white text-font-caption2-narrow">
                        {I18n.t("MSG_ASSET_ALBUM_ADD_TITLE")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* AIタブ: 生成ボタン */}
              {pickerTab === StockTabIndex.AI && (
                <div className="flex-col-el flex-center upsilon">
                  <div
                    onClick={handleOpenAIModal}
                    className="btn btn-primary !p-0 !m-0 w-[120px] h-[120px] cursor-pointer bg-blue-100 rounded-lg"
                  >
                    <div className="flex-col-center w-full h-full">
                      <span className="material-symbols-outlined text-white !text-[40px]">
                        auto_awesome
                      </span>
                      <p className="text-body text-white text-font-caption2-narrow">
                        {I18n.t("MSG_AI_TAB_GENERATE")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* アセット一覧 */}
              {filteredAssets
                .sort((a, b) => (dayjs(a.created_at) < dayjs(b.created_at) ? 1 : -1))
                .map((asset) => (
                  <div
                    key={asset.id}
                    onClick={handleSelectAsset(asset)}
                    className="flex-col-el flex-center w-[120px] h-[120px] bg-beige-20 border-2 border-solid border-transparent cursor-pointer hover:border-green-100 active:scale-95 transition-all overflow-hidden"
                  >
                    <img
                      alt={asset.name}
                      className="w-full h-full object-contain p-1"
                      src={asset.url}
                      loading="lazy"
                    />
                  </div>
                ))}
            </div>

            {/* 空状態 */}
            {filteredAssets.length === 0 &&
              pickerTab !== StockTabIndex.ALBUM &&
              pickerTab !== StockTabIndex.AI && (
                <div className="flex-col-el flex-center w-full h-full">
                  <p className="text-body text-gray-80 text-font-caption2-narrow">
                    {getEmptyMessage()}
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* AI生成モーダル */}
      {user && (
        <AIGenerateOnlyModal open={showAIModal} onClose={handleCloseAIModal} uid={user.uid} />
      )}
    </>
  );
};
